import { useState } from 'react';
import { TESTIMONIALS, type TestimonialItem } from '@/data/testimonials';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';

const QUOTE = '/landing/testimonials/quote.svg';

const HEADING_PARAGRAPH =
    'Behind every case resolved, every right protected, and every voice heard is a person who finally found a way forward. These are the people who lived it.';

/** Where the four visible cards sit on the desktop frame; the second is the featured one. */
const CARD_LEFTS = [144, 934, 1284, 1634];
const FEATURED = 1;

const Chevron = ({ direction }: { direction: 'left' | 'right' }) => (
    <span className="flex size-[41px] items-center justify-center rounded-full bg-[#f2f2f2]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-[#1d1b20]">
            <path
                d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </span>
);

const ArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
    <span className="flex size-[29px] items-center justify-center">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#1d1b20]">
            <path
                d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </span>
);

/** A portrait, or the person's initials until one is uploaded. */
function Portrait({ item, className }: { item: TestimonialItem; className?: string }) {
    return item.avatar ? (
        <img
            src={item.avatar}
            alt={item.name}
            className={`absolute inset-0 size-full object-cover object-top [mix-blend-mode:luminosity] ${className ?? ''}`}
        />
    ) : (
        <span
            className={`absolute inset-0 flex items-center justify-center font-primary text-[40px] font-semibold text-[#8a8a8a] ${className ?? ''}`}
        >
            {item.initials}
        </span>
    );
}

function TestimonialCard({
    item,
    left,
    dim,
    onSelect,
}: {
    item: TestimonialItem;
    left: number;
    dim: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={!dim}
            aria-label={`Show ${item.name}`}
            className="absolute top-[592.93px] h-[276.072px] w-[231px] text-left"
            style={{ left }}
        >
            <div className="absolute left-[0.17px] top-0 h-[219.43px] w-[216.802px] overflow-hidden">
                <div
                    className={`absolute left-[1.49px] top-0 h-[219.43px] w-[215.488px] bg-[#d9d9d9] ${dim ? 'opacity-[0.62]' : ''}`}
                />
                <Portrait item={item} className={dim ? 'opacity-[0.62]' : ''} />
            </div>
            <p className="absolute left-0 top-[238.57px] w-[231px] -translate-y-1/2 text-[20px] font-bold leading-[32px] text-[#333]">
                {item.name}
            </p>
            {item.role && (
                <p className="absolute left-0 top-[263.57px] w-[231px] -translate-y-1/2 text-[14px] font-normal leading-[24px] text-[#f60]">
                    {item.role}
                </p>
            )}
        </button>
    );
}

/** The visible cards: the featured one in the second slot, its neighbours around it, wrapping. */
function visibleCards(
    items: TestimonialItem[],
    active: number,
): { item: TestimonialItem; index: number; left: number }[] {
    const n = items.length;
    return CARD_LEFTS.slice(0, n).map((left, slot) => {
        const index = (((active + slot - FEATURED) % n) + n) % n;
        return { item: items[index], index, left };
    });
}

function TestimonialsDesktop({ items }: { items: TestimonialItem[] }) {
    const [active, setActive] = useState(0);
    const featured = items[active];
    const go = (step: number) => setActive((i) => (i + step + items.length) % items.length);
    return (
        <FigmaFrame designWidth={1920} designHeight={1080} className="w-full">
            <div className="relative h-[1080px] w-[1920px] bg-white font-primary">
                {/* Right gray panel */}
                <div className="absolute left-[1415px] top-0 h-[823px] w-[505px] bg-[#f4f4f4]" />

                {/* Heading + intro */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[170px] top-[101px] flex w-[448px] flex-col items-start gap-[27px]"
                >
                    <p className="whitespace-nowrap text-[96px] font-extrabold leading-[103px] text-[#1b1b1b]">
                        Testimonials<span className="text-[#f60]">.</span>
                    </p>
                    <p className="w-full text-[14px] font-normal leading-[24px] text-[#333]">{HEADING_PARAGRAPH}</p>
                </Reveal>

                {/* Big quote mark illustration */}
                <Reveal
                    as="div"
                    once
                    variant="scale"
                    duration={1100}
                    className="absolute left-[494px] top-[372.19px] h-[654.346px] w-[433.603px]"
                >
                    <img src={QUOTE} alt="" className="absolute inset-0 size-full max-w-none" />
                </Reveal>

                {/* Featured quote text */}
                <Reveal
                    as="p"
                    once
                    variant="fade-up"
                    delay={200}
                    className="absolute left-[773px] top-[466px] h-[170px] w-[477px] -translate-y-1/2 text-[24px] font-normal leading-[32px] text-[#333]"
                >
                    <span key={active} className="cg-sol-enter block">
                        {featured.quote}
                    </span>
                </Reveal>

                {/* Testimonial cards */}
                {visibleCards(items, active).map(({ item, index, left }, slot) => (
                    <Reveal key={`${item.name}-${slot}`} once variant="fade-left" delay={slot * 110}>
                        <TestimonialCard
                            item={item}
                            left={left}
                            dim={index !== active}
                            onSelect={() => setActive(index)}
                        />
                    </Reveal>
                ))}

                {/* Carousel arrows */}
                {items.length > 1 && (
                    <div className="absolute left-[934px] top-[900px] flex gap-[22px]">
                        <button type="button" aria-label="Previous testimonial" onClick={() => go(-1)}>
                            <Chevron direction="left" />
                        </button>
                        <button type="button" aria-label="Next testimonial" onClick={() => go(1)}>
                            <Chevron direction="right" />
                        </button>
                    </div>
                )}
            </div>
        </FigmaFrame>
    );
}

function TestimonialsMobile({ items }: { items: TestimonialItem[] }) {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);
    const t = items[index];

    const go = (step: number) => {
        setDir(step);
        setIndex((i) => (i + step + items.length) % items.length);
    };

    return (
        <section className="bg-white font-primary">
            <div className="relative mx-auto h-[597px] w-full max-w-[375px]">
                {/* Heading */}
                <h2 className="absolute left-5 top-[38px] w-[301px] text-[40px] font-extrabold leading-[50px] text-[#1b1b1b]">
                    Testimonials<span className="text-[#f60]">.</span>
                </h2>

                {/* Heading paragraph */}
                <p className="absolute left-5 top-[97px] w-[335px] text-[14px] font-normal leading-[24px] text-[#333]">
                    {HEADING_PARAGRAPH}
                </p>

                {/* Big quote mark illustration (static) */}
                <div className="absolute left-5 top-[342px] h-[217px] w-[143.795px]">
                    <img src={QUOTE} alt="" className="block size-full max-w-none" />
                </div>

                {/* Swapping content: quote, photo, name, role */}
                <div
                    key={index}
                    className="cg-sol-enter"
                    style={{ '--cg-sol-dir': `${dir * 32}px` } as React.CSSProperties}
                >
                    {/* Featured quote text */}
                    <p className="absolute left-[120px] top-[249px] w-[237px] text-[14px] font-normal leading-[24px] text-[#333]">
                        {t.quote}
                    </p>

                    {/* Author card */}
                    <div className="absolute left-[173px] top-[415px] h-[111px] w-[154px]">
                        <div className="absolute left-0 top-0 h-[70.674px] w-[69.827px] overflow-hidden bg-[#d9d9d9]">
                            <Portrait item={t} className="text-[22px]" />
                        </div>
                        <p className="absolute left-0 top-[75px] w-[154px] text-[14px] font-bold leading-[32px] text-[#333]">
                            {t.name}
                        </p>
                        {t.role && (
                            <p className="absolute left-0 top-[96px] w-[154px] text-[12px] font-normal leading-[24px] text-[#f60]">
                                {t.role}
                            </p>
                        )}
                    </div>
                </div>

                {/* Carousel arrows */}
                <div className="absolute left-[281px] top-[530px] flex gap-[16px]">
                    <button type="button" aria-label="Previous testimonial" onClick={() => go(-1)}>
                        <ArrowIcon direction="left" />
                    </button>
                    <button type="button" aria-label="Next testimonial" onClick={() => go(1)}>
                        <ArrowIcon direction="right" />
                    </button>
                </div>
            </div>
        </section>
    );
}

/** Read at render so the hydrated list is what shows; nothing to show, nothing rendered. */
export default function Testimonials() {
    const items = TESTIMONIALS;
    if (!items.length) return null;
    return (
        <>
            <div className="hidden lg:block">
                <TestimonialsDesktop items={items} />
            </div>
            <div className="lg:hidden">
                <TestimonialsMobile items={items} />
            </div>
        </>
    );
}
